import React, { useEffect, memo, useState, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import useRecentSalary from "@hooks/formation/useRecentSalary";
import { useMultipleFileUpload } from "@hooks/index";
import {
  courseAndSalaryActions,
  employeFormationActions,
  formationActions,
} from "@store/actions";
import moment from "moment-timezone";
import { useForm, Controller } from "react-hook-form";

import "@assets/css/pages/employer/listeEmployer.css";

import ModalIndex from "@Modals/layout/index";
import DateRangePickerDropdown from "@components/dateRange/DropdownDateRange";
import InputWithAddOptions from "@components/input/InputWithAddOptions";
import {
  ListboxSimple,
  Input,
  InputGroup,
  TextArea,
} from "@components/tailwindComponents";
import { DropdownDevise } from "@components/tailwindComponents";

import {
  convertToNumericString,
  checkEnterpriseId,
  getDateToSave,
  getDateToRender,
  mapEmplacementToDevise,
} from "@helpers/_functions";

import useFormation, { useFormationOption } from "../../hooksFormation";
import UploadFile from "../UploadFile";
import { useReduxSelector } from "@hooks/useReduxSelector";

/**
 * @param {Object} props
 * @param {boolean} props.show
 * @param {Function} props.onClickClose
 * @param {any} props.formation
 * @param {boolean} props.isOnMyProfile
 */
function ModalFormationForm({ show, onClickClose, formation = {} , isOnMyProfile}) {
  const { t } = useTranslation("formation.formation");

  const isUpdate = typeof formation?._id !== "undefined";
  const {
    categories,
    trainers,
    statusList,
    ellibilityOptions,
    upsertCategory,
    upsertStatus,
    upsertTrainer,
    deleteOption,
  } = useFormationOption();

  const { _id: employeeId } = useParams();
  const reduxDispatch = useDispatch();

  const { upsertFormation, handleFormErrors } = useFormation();

  const {
    reset,
    control,
    watch,
    handleSubmit,
    setValue,
    resetField,
    getValues
  } = useForm();

  const emplacementList = useReduxSelector(
    ({ commonData }) => commonData.emplacements
  );
  const deviseList = useMemo(
    () => mapEmplacementToDevise(emplacementList),
    [emplacementList],
  );

  useEffect(() => {
    if (show) {
      if (!isUpdate) {
        setValue("deviseFormValue", deviseList?.[0] || {});
      } else if (typeof formation._id !== "undefined") {
        const deviseFormValue = formation.devise ? { _id: formation.devise, name: formation.devise } : {};
        const startEndDate = [
          {
            startDate: moment(getDateToRender(formation.startDate)).toDate(),
            endDate: moment(getDateToRender(formation.endDate)).toDate(),
          },
        ];
        // const hour = !isNaN(formation.hour) ? formation.hour : moment(formation.hour).format("HH")

        const resetValue = {
          ...formation,
          costSalary: formation.costSalary ?? 0,
          costTrainer: formation.costTrainer ?? 0,
          costTravel: formation.costTravel ?? 0,
          costOthers: formation.costOthers ?? 0,
          deviseFormValue,
          startEndDate,
          // hour
        };

        if (Array.isArray(ellibilityOptions) && formation.elligibility) {
          resetValue.elligibility = ellibilityOptions.find(
            (e) => e._id === formation.elligibility,
          );
        }

        reset(resetValue);
      }
    } else {
      removeOldFiles();
      reset({ title: "" }); // bug maj valeurs si sans params ici
      resetInputFiles();
      resetData();
    }
  }, [show]);

  const refreshFormationsInApp = () => {
    reduxDispatch(employeFormationActions.refreshData);
    reduxDispatch(formationActions.refreshData);
  };

  const {
    oldFiles,
    handleDeleteOldFiles,
    removeOldFiles,
    toDeleteOldFiles,
    allFileDone,
    resetData,
    handleUploadStatusChange,
    resetInputFiles,
  } = useMultipleFileUpload({
    defaultFiles: formation?.files,
    onDeleteOldFile: refreshFormationsInApp,
  });

  const [isSaving, setIsSaving] = useState(false);
  const callUpsertFormation = async (data) => {
    try {
      setIsSaving(true);
      const { startDate, endDate } = data.startEndDate?.[0] || {};
      const formationToSave = {
        ...data,
        startDate: moment(getDateToSave(startDate)).toDate(),
        endDate: moment(getDateToSave(endDate)).toDate(),
        devise: data.deviseFormValue?.name,
        elligibility: data.elligibility?._id,
        category: data.category?._id ? data.category._id : data.category,
        trainer: data.trainer?._id ? data.trainer._id : data.trainer,
        status: data.status?._id ? data.status._id : data.status,
        Enterprise: checkEnterpriseId(),
        Employer: employeeId,
        isOnMyProfile: isOnMyProfile
      };
      if (toDeleteOldFiles?.length) {
        await removeOldFiles();
      }
      if (formation._id) {
        formationToSave._id = formation._id;
      }
      const onSuccess = (_) => {
        onClickClose();
      };
      await upsertFormation(formationToSave, onSuccess);
    } catch (error) {
      console.log(error);
    }

    setIsSaving(false);
  };

  const costSalary = watch("costSalary", 0);
  const costTravel = watch("costTravel", 0);
  const costTrainer = watch("costTrainer", 0);
  const costOthers = watch("costOthers", 0);
  const hour = watch("hour", 0);
  const hourManualChange = watch("hourManualChange", false);
  const recentSalary = useRecentSalary(employeeId, [], {});

  useEffect(() => {
    if (employeeId) {
      reduxDispatch(courseAndSalaryActions.loadData(employeeId));
    }
  }, [employeeId, reduxDispatch]);

  useEffect(() => {
    const costTotal = +costSalary + +costTravel + +costTrainer + +costOthers;
    setValue("costTotal", costTotal);
  }, [costSalary, costTravel, costTrainer, costOthers]);

  useEffect(() => {
    const calculateCostSalary = (hourValue) => {
      if (recentSalary?.hourlyRate) {
        const hourlyRate = parseFloat(recentSalary.hourlyRate || 0);
        const newCostSalary =
          (!isNaN(hourValue) ? hourValue : moment(hourValue).format("HH")) *
          hourlyRate;
        setValue("costSalary", newCostSalary);
      }
    };

    if (hourManualChange) {
      calculateCostSalary(hour);
    }
  }, [recentSalary?.hourlyRate, hour, hourManualChange, setValue]);

  useEffect(() => {
    if (employeeId && recentSalary?.hourlyRateDevise) {
      const hourlyRateDevise = recentSalary?.hourlyRateDevise;
      setValue("deviseFormValue", {
        id: hourlyRateDevise,
        name: hourlyRateDevise,
      });
    }
  }, [recentSalary?.hourlyRate, setValue]);

  const titleModal = !isUpdate
    ? t("Ajouter une formation")
    : t("Modifier la formation");

  return (
    <ModalIndex
      isOpen={show}
      onClickClose={onClickClose}
      title={titleModal}
      mustWaiting={!allFileDone}
      buttonOptions={{
        click: handleSubmit(callUpsertFormation, handleFormErrors),
        color: "green",
        title: t("Soumettre"),
      }}
      buttonCancelOptions={{
        click: () => onClickClose(),
        color: "outlined",
        title: t("Annuler"),
      }}
      isLoading={isSaving}
    >
      <div className="!arh-mt-0">
        <div>
          <Controller
            control={control}
            name="title"
            rules={{ required: true }}
            render={({
              field: { onChange, value, name },
              fieldState: { invalid },
            }) => (
              <Input
                label={t("Titre formation")}
                placeholder={t("Titre formation")}
                value={value}
                onChange={onChange}
                name={name}
                invalid={invalid}
              />
            )}
          />
        </div>
        <div className="arh-mt-2 arh-w-full">
          <Controller
            control={control}
            name="category"
            render={({
              field: { onChange, value, name },
              fieldState: { invalid },
            }) => (
              <>
                <label
                  className={` arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium 
                        ${invalid ? " arh-text-[#F60A20] " : " arh-text-black "}
                    `}
                >
                  {t("Catégorie")}
                </label>
                <InputWithAddOptions
                  defaultText={t("Catégorie")}
                  defaultSelected={value?._id}
                  optionsList={categories}
                  secondDefaultText={t("Ajouter")}
                  name={name}
                  value={value?.name}
                  optionListLabel="name"
                  onChange={(e) => onChange(e.target.dataset.id)}
                  addItem={upsertCategory}
                  editItem={upsertCategory}
                  deleteItem={deleteOption}
                  validateDropdown={invalid}
                  showAction
                  classParentList="arh-mb-[0.75rem]"
                  hideEdit={true}
                />
              </>
            )}
          />
          <div className="datepicker_modals_parcours arh-w-full ">
            <Controller
              control={control}
              name="startEndDate"
              defaultValue={[]}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { invalid },
              }) => (
                <>
                  <label
                    className={` arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium 
                            ${invalid
                        ? " arh-text-[#F60A20] "
                        : " arh-text-black "
                      }
                        `}
                  >
                    {t("Periode")}
                  </label>
                  <div className="dateRange_green DateRangePicker_customisation DateRangePicker_customisation--green arh-relative">
                    <DateRangePickerDropdown
                      key="date_range_periode"
                      classParent="arhDatePicker"
                      idInput="daterange"
                      className={
                        value?.[0]
                          ? "input_simple "
                          : "input_simple !arh-pl-[1.25rem] "
                      }
                      dateSelected={value?.[0]}
                      textInput={t("Période")}
                      SaveCloseRange={onChange}
                      showResetFilter={
                        getValues("startEndDate")?.startDate &&
                          getValues("startEndDate")?.endDate
                          ? true
                          : false
                      }
                      onResetFilter={() => resetField("startEndDate")}
                      classSwitch="c-switch-pill-checked--green--color--to--left"
                      classBackground="green"
                      invalid={invalid}
                    />
                  </div>
                </>
              )}
            />
          </div>
          <div className="arh-mt-2 arh-w-full">
            <Controller
              control={control}
              name="trainer"
              render={({
                field: { onChange, value, name },
                fieldState: { invalid },
              }) => (
                <>
                  <label
                    className={` arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium 
                      ${invalid ? " arh-text-[#F60A20] " : " arh-text-black "}
                  `}
                  >
                    {t("Formateur")}
                  </label>
                  <InputWithAddOptions
                    defaultText={t("Formateur")}
                    defaultSelected={value?._id}
                    optionsList={trainers}
                    secondDefaultText={t("Ajouter")}
                    name={name}
                    value={value?.name}
                    optionListLabel="name"
                    onChange={(e) => onChange(e.target.dataset.id)}
                    addItem={upsertTrainer}
                    editItem={upsertTrainer}
                    deleteItem={deleteOption}
                    validateDropdown={invalid}
                    showAction
                    classParentList="arh-mb-[0.5rem]"
                    hideEdit={true}
                  />
                </>
              )}
            />
          </div>
          <div className=" arh-w-full">
            <Controller
              control={control}
              rules={{ required: true }}
              name="hour"
              render={({
                field: { onChange, value },
                fieldState: { invalid },
              }) => (
                <InputGroup
                  name={name}
                  content="H"
                  label={t("Heures")}
                  placeholder={t("Heures")}
                  onChange={(e) => {
                    onChange(convertToNumericString(e.target.value));
                    setValue("hourManualChange", true);
                  }}
                  value={value}
                  invalid={invalid}
                  classContent="arh-w-12"
                />
              )}
            />
          </div>
          <div className="arh-flex arh-w-full arh-items-center arh-justify-between ">
            <div className=" arh-w-[23%] ">
              <Controller
                control={control}
                name="costSalary"
                defaultValue={0}
                render={({
                  field: { onChange, value, name },
                  fieldState: { invalid },
                }) => (
                  <Input
                    value={value}
                    onChange={(e) => {
                      onChange(convertToNumericString(e.target.value));
                    }}
                    name={name}
                    label={t("Coût salaire")}
                    // placeholder={t("Coût salaire")}
                    invalid={invalid}
                  />
                )}
              />
            </div>
            <div className=" arh-w-[23%]">
              <Controller
                control={control}
                name="costTrainer"
                defaultValue={0}
                render={({
                  field: { onChange, value, name },
                  fieldState: { invalid },
                }) => (
                  <Input
                    value={value}
                    onChange={(e) => {
                      onChange(convertToNumericString(e.target.value));
                    }}
                    name={name}
                    label={t("Coût formateur")}
                    // placeholder={t("Coût formateur")}
                    invalid={invalid}
                  />
                )}
              />
            </div>
            <div className=" arh-w-[23%]">
              <Controller
                control={control}
                name="costTravel"
                defaultValue={0}
                render={({
                  field: { onChange, value, name },
                  fieldState: { invalid },
                }) => (
                  <Input
                    value={value}
                    onChange={(e) => {
                      onChange(convertToNumericString(e.target.value));
                    }}
                    name={name}
                    label={t("Coût voyage")}
                    // placeholder={t("Coût voyage")}
                    invalid={invalid}
                  />
                )}
              />
            </div>
            <div className=" arh-w-[23%]">
              <Controller
                control={control}
                name="costOthers"
                defaultValue={0}
                render={({
                  field: { onChange, value, name },
                  fieldState: { invalid },
                }) => (
                  <Input
                    value={value}
                    onChange={(e) => {
                      onChange(convertToNumericString(e.target.value));
                    }}
                    name={name}
                    label={t("Autres coûts")}
                    // placeholder={t("Autes coûts")}
                    invalid={invalid}
                  />
                )}
              />
            </div>
          </div>

          <div className="arh-mb-[1.5rem] arh-w-full ">
            <Controller
              control={control}
              name="costTotal"
              defaultValue={0}
              render={({
                field: { onChange, value, name },
                fieldState: { invalid },
              }) => (
                <InputGroup
                  name={name}
                  onChange={(e) => {
                    onChange(convertToNumericString(e.target.value));
                  }}
                  value={value}
                  invalid={invalid}
                  disabled
                  placeholder={t("Coût total")}
                  label={t("Coût total")}
                  classParent="arh-w-full"
                  classContent="arh-w-24 !arh-bg-white"
                  content={
                    <DropdownDevise
                      name="deviseFormValue"
                      control={control}
                      setValue={setValue}
                    // disable={recentSalary?.hourlyRateDevise}
                    />
                  }
                />
              )}
            />
          </div>
          <div className="arh-w-full">
            <div className="arh-flex arh-items-center arh-justify-between">
              <Controller
                control={control}
                name="elligibility"
                defaultValue={null}
                rules={{ required: true }}
                render={({
                  field: { onChange, value, name },
                  fieldState: { invalid },
                }) => (
                  <ListboxSimple
                    placeholder={t("Admissibilité")}
                    name={name}
                    labelActive={true}
                    label={t("Admissibilité")}
                    optionList={ellibilityOptions || []}
                    selectedValue={value}
                    setSelectedValue={onChange}
                    //showReset={!!value}
                    invalid={invalid}
                    noRadio={true}
                    withTooltip={true}
                  />
                )}
              />
            </div>
            <div className="arh-mt-2 arh-w-full">
              <Controller
                control={control}
                name="status"
                render={({
                  field: { onChange, value, name },
                  fieldState: { invalid },
                }) => (
                  <>
                    <label
                      className={` arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium 
                      ${invalid ? " arh-text-[#F60A20] " : " arh-text-black "}
                  `}
                    >
                      {t("Status")}
                    </label>
                    <InputWithAddOptions
                      defaultText={t("Status")}
                      defaultSelected={value?._id}
                      optionsList={statusList}
                      secondDefaultText={t("Ajouter")}
                      name={name}
                      value={value?.name}
                      optionListLabel="name"
                      onChange={(e) => onChange(e.target.dataset.id)}
                      addItem={upsertStatus}
                      editItem={upsertStatus}
                      deleteItem={deleteOption}
                      validateDropdown={invalid}
                      showAction
                      // classParentList="arh-mb-[1.75rem]"
                      hideEdit={true}
                    />
                  </>
                )}
              />
            </div>
          </div>

          <div className="arh-my-[0.75rem] ">
            <Controller
              control={control}
              defaultValue=""
              name="note"
              render={({ field: { onChange, value, name } }) => (
                <TextArea
                  label={t("Note")}
                  placeholder={t("Note")}
                  value={value}
                  onChange={onChange}
                  name={name}
                />
              )}
            />
          </div>

          <UploadFile
            t={t}
            control={control}
            oldFiles={oldFiles}
            isDefaultOpen={formation.files?.length > 0}
            handleUploadStatusChange={handleUploadStatusChange}
            handleDeleteOldFiles={handleDeleteOldFiles}
          />
        </div>
      </div>
    </ModalIndex>
  );
}

export default memo(ModalFormationForm);
